/**
 * Artifact Service
 *
 * Fetches a fresh, short-lived presigned download URL for a report artifact at
 * click time. The sessions API mints a ~2-minute presigned GET for the one
 * artifact identified by (runId, name) within the caller's own session, so the
 * download link is always valid — even when reopening an old conversation whose
 * originally-streamed presigned URL has long since expired.
 *
 * The artifact objects live in a PRIVATE S3 bucket; the presigned URL (signed
 * in its query string, no Authorization header needed) is the only read grant,
 * and the endpoint verifies session ownership before signing.
 */

// The sessions routes live on the same REST API as the feedback endpoint, whose
// root (ending in "/") is published as `feedbackApiUrl` in aws-exports.json.
let cachedBaseUrl = ""

async function loadBaseUrl(): Promise<string> {
  if (cachedBaseUrl) return cachedBaseUrl
  const response = await fetch("/aws-exports.json")
  const config = await response.json()
  if (!config.feedbackApiUrl) {
    throw new Error("Artifact API URL not configured (feedbackApiUrl missing)")
  }
  cachedBaseUrl = config.feedbackApiUrl as string
  return cachedBaseUrl
}

/**
 * Fetch a fresh presigned download URL for one artifact in a session.
 *
 * @param sessionId - the conversation the artifact belongs to.
 * @param runId - the artifact's run id (from the artifact segment).
 * @param name - the artifact's display filename (from the artifact segment).
 * @param idToken - Cognito ID token for the API Gateway authorizer.
 * @returns the fresh presigned GET URL.
 * @throws if unauthenticated, the artifact is not found, or the request fails.
 */
export async function fetchArtifactDownloadUrl(
  sessionId: string,
  runId: string,
  name: string,
  idToken: string
): Promise<string> {
  const base = await loadBaseUrl()
  const query = `artifactRun=${encodeURIComponent(runId)}&artifactName=${encodeURIComponent(name)}`
  const url = `${base}sessions/${encodeURIComponent(sessionId)}?${query}`
  const response = await fetch(url, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
  })
  if (!response.ok) {
    const detail = await response.json().catch(() => ({}))
    throw new Error(detail.error || `HTTP ${response.status}`)
  }
  const data = (await response.json()) as { url?: string }
  if (!data.url) {
    throw new Error("No download URL returned")
  }
  return data.url
}
